import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateContact, updateAdress, setUser } from "../user/userSlice";
import toast from "react-hot-toast";
import { Pencil } from "lucide-react";
import { isEmail, isPhone } from "../../utils/validation";
import { formatPhone } from "../../utils/phone";
import PhoneField from "../../components/common/PhoneField";
import NewPasswordModal from "../../components/common/modals/NewPasswordModal";
import EmailChangeModal from "../../components/common/modals/ChangeEmailModal";
import ChangeNumberModal from "../../components/common/modals/ChangeNumberModal";

const CONTACT_FIELDS = [
  { key: "vorname", label: "Vorname" },
  { key: "nachname", label: "Nachname" },
  { key: "telefonnummer", label: "Telefonnummer" },
];

const ADDRESS_FIELDS = [
  { key: "land", label: "Land" },
  { key: "ort", label: "Ort" },
  { key: "strasse", label: "Strasse" },
  { key: "plz", label: "PLZ" },
];

export default function Main() {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [editAddressMode, setEditAddressMode] = useState(false);

  const [originalContactData, setOriginalContactData] = useState(null);
  const [originalAddressData, setOriginalAddressData] = useState(null);

  const [invalidFields, setInvalidFields] = useState([]);
  const [invalidAddressFields, setInvalidAddressFields] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    vorname: "",
    nachname: "",
    email: "",
    telefonnummer: "",
    land: "",
    ort: "",
    strasse: "",
    plz: "",
  });

  useEffect(() => {
    setLoading(true);
    if (user) {
      setFormData({
        vorname: user.vorname ?? "",
        nachname: user.nachname ?? "",
        email: user.email ?? "",
        telefonnummer: user.telefonnummer ?? "",
        land: user.land ?? "",
        ort: user.ort ?? "",
        strasse: user.strasse ?? "",
        plz: user.plz ?? "",
      });
    } else {
      const saved = localStorage.getItem("user");
      if (saved) {
        dispatch(setUser(JSON.parse(saved)));
      }
    }
    setLoading(false);
  }, [user, dispatch]);

  const validateForm = (data, requiredFields) => {
    const invalid = [];
    requiredFields.forEach((field) => {
      const value = data[field];
      if (field === "telefonnummer") {
        if (!isPhone(value)) invalid.push(field);
      } else if (field === "email") {
        if (!isEmail(value)) invalid.push(field);
      } else {
        if (typeof value !== "string" || value.trim() === "")
          invalid.push(field);
      }
    });
    return invalid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditStart = () => {
    setOriginalContactData({
      vorname: formData.vorname,
      nachname: formData.nachname,
      email: formData.email,
      telefonnummer: formData.telefonnummer,
    });
    setEditMode(true);
  };

  const handleEditCancel = () => {
    if (originalContactData) {
      setFormData((prev) => ({
        ...prev,
        ...originalContactData,
      }));
    }
    setOriginalContactData(null);
    setInvalidFields([]);
    setEditMode(false);
  };

  const handleContactSave = () => {
    const required = ["vorname", "nachname", "email", "telefonnummer"];
    const invalid = validateForm(formData, required);
    setInvalidFields(invalid);
    if (invalid.length > 0) {
      toast.error("Bitte füllen Sie alle Kontaktfelder korrekt aus.");
      return;
    }

    setEditMode(false);
    setLoading(true);
    setOriginalContactData(null);

    dispatch(
      updateContact({
        id: user.idUser,
        vorname: formData.vorname,
        nachname: formData.nachname,
        email: formData.email,
        telefonnummer: formData.telefonnummer,
      }),
    )
      .unwrap()
      .then((res) => {
        dispatch(setUser(res.updatedUser));
        localStorage.setItem("user", JSON.stringify(res.updatedUser));
        setFormData((prev) => ({
          ...prev,
          vorname: res.updatedUser.vorname ?? "",
          nachname: res.updatedUser.nachname ?? "",
          email: res.updatedUser.email ?? "",
          telefonnummer: res.updatedUser.telefonnummer ?? "",
        }));
        setLoading(false);
        window.location.reload();
        toast.success("Erfolgreich gespeichert");
      })
      .catch(() => toast.error("Fehler beim Speichern der Daten"));
  };

  const handleAddressEditStart = () => {
    setOriginalAddressData({
      land: formData.land,
      ort: formData.ort,
      strasse: formData.strasse,
      plz: formData.plz,
    });
    setEditAddressMode(true);
  };

  const handleAddressCancel = () => {
    if (originalAddressData) {
      setFormData((prev) => ({
        ...prev,
        ...originalAddressData,
      }));
    }
    setOriginalAddressData(null);
    setInvalidAddressFields([]);
    setEditAddressMode(false);
  };

  const handleAddressSave = () => {
    const required = ["land", "ort", "strasse", "plz"];
    const invalid = validateForm(formData, required);
    setInvalidAddressFields(invalid);
    if (invalid.length > 0) {
      toast.error("Bitte füllen Sie alle Adressfelder korrekt aus.");
      return;
    }

    setEditAddressMode(false);
    setLoading(true);
    setOriginalAddressData(null);

    dispatch(
      updateAdress({
        id: user.idUser,
        land: formData.land,
        ort: formData.ort,
        strasse: formData.strasse,
        plz: formData.plz,
      }),
    )
      .unwrap()
      .then((res) => {
        dispatch(setUser(res.updatedUser));
        localStorage.setItem("user", JSON.stringify(res.updatedUser));
        setFormData((prev) => ({
          ...prev,
          land: res.updatedUser.land ?? "",
          ort: res.updatedUser.ort ?? "",
          strasse: res.updatedUser.strasse ?? "",
          plz: res.updatedUser.plz ?? "",
        }));
        setLoading(false);
        window.location.reload();
        toast.success("Erfolgreich gespeichert");
      })
      .catch(() => toast.error("Fehler beim Speichern der Daten"));
  };

  if (!user) {
    return (
      <div className="kn-page flex justify-center items-center mt-24 w-full">
        <h1 className="kn-h2">Bitte melden Sie sich an.</h1>
      </div>
    );
  }

  return (
    <div className="kn-page">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-2 mt-1 h-[2px] bg-gray-300 opacity-80 rounded-full animate-pulse" />
        </div>
      )}

      {modalOpen && (
        <NewPasswordModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          idUser={user.idUser}
        />
      )}

      {emailModalOpen && (
        <EmailChangeModal
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          idUser={user.idUser}
          vorname={user.vorname}
        />
      )}

      <main className="max-w-[1000px] mx-auto px-5 sm:px-8 lg:px-12 pt-11 pb-20">
        <div className="kn-eyebrow">Konto</div>
        <h1 className="kn-h1 mt-3 mb-7">Willkommen, {user.vorname}!</h1>

        {/* Kontaktinformationen */}
        <section className="kn-card p-6 mb-5">
          <div className="flex flex-wrap gap-3 items-center justify-between pb-[18px] border-b border-knapp-border">
            <h2 className="kn-h3">Kontaktinformationen</h2>
            <div className="flex gap-2.5 flex-wrap">
              {editMode ? (
                <>
                  <button
                    onClick={handleEditCancel}
                    className="kn-btn-ghost text-sm py-2.5 px-6"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleContactSave}
                    className="kn-btn-primary text-sm py-2.5 px-6"
                  >
                    Speichern
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEditStart}
                  className="kn-btn-primary text-sm py-2.5 px-6"
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  Bearbeiten
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 mt-5">
            {CONTACT_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <div className="text-[13px] text-knapp-ink4 tracking-[0.04em]">
                  {label}
                </div>
                {editMode && key === "telefonnummer" ? (
                  <div className="mt-1.5">
                    <PhoneField
                      value={formData.telefonnummer}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          telefonnummer: value,
                        }))
                      }
                      invalid={invalidFields.includes(key)}
                    />
                  </div>
                ) : editMode ? (
                  <input
                    type="text"
                    name={key}
                    className={`kn-input mt-1.5 ${
                      invalidFields.includes(key) ? "border-knapp-accent" : ""
                    }`}
                    value={formData[key] ?? ""}
                    onChange={handleChange}
                  />
                ) : (
                  <div className="text-[17px] font-semibold mt-1 break-words">
                    {key === "telefonnummer"
                      ? formatPhone(formData[key])
                      : formData[key]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Adresse */}
        <section className="kn-card p-6 mb-5">
          <div className="flex flex-wrap gap-3 items-center justify-between pb-[18px] border-b border-knapp-border">
            <h2 className="kn-h3">Adresse</h2>
            <div className="flex gap-2.5 flex-wrap">
              {editAddressMode ? (
                <>
                  <button
                    onClick={handleAddressCancel}
                    className="kn-btn-ghost text-sm py-2.5 px-6"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleAddressSave}
                    className="kn-btn-primary text-sm py-2.5 px-6"
                  >
                    Speichern
                  </button>
                </>
              ) : (
                <button
                  onClick={handleAddressEditStart}
                  className="kn-btn-primary text-sm py-2.5 px-6"
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  Bearbeiten
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 mt-5">
            {ADDRESS_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <div className="text-[13px] text-knapp-ink4 tracking-[0.04em]">
                  {label}
                </div>
                {editAddressMode ? (
                  <input
                    type="text"
                    name={key}
                    className={`kn-input mt-1.5 ${
                      invalidAddressFields.includes(key)
                        ? "border-knapp-accent"
                        : ""
                    }`}
                    value={formData[key] ?? ""}
                    onChange={handleChange}
                  />
                ) : (
                  <div className="text-[17px] font-semibold mt-1 break-words">
                    {formData[key]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Login & Sicherheit */}
        <section className="kn-card p-6">
          <h2 className="kn-h3 pb-[18px] border-b border-knapp-border">
            Login &amp; Sicherheit
          </h2>

          <div className="flex flex-wrap gap-4 items-center justify-between py-4">
            <div className="min-w-0">
              <div className="text-[13px] text-knapp-ink4 tracking-[0.04em]">
                E-Mail-Adresse
              </div>
              <div className="text-[17px] font-semibold mt-1 break-words">
                {formData.email}
              </div>
            </div>
            <button
              onClick={() => setEmailModalOpen(true)}
              className="kn-btn-outline text-sm py-2.5 px-6"
            >
              E-Mail ändern
            </button>
          </div>

          <div className="flex flex-wrap gap-4 items-center justify-between py-4 border-t border-knapp-borderSubtle">
            <div>
              <div className="text-[13px] text-knapp-ink4 tracking-[0.04em]">
                Passwort
              </div>
              <div className="text-[19px] font-semibold tracking-[0.18em] mt-1.5">
                ••••••••
              </div>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="kn-btn-outline text-sm py-2.5 px-6"
            >
              Passwort ändern
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
